// Wildcard ("*" module row) expansion for navigation and permission derivation.
// LEAF module (relative imports only) so it is unit-testable like roleCatalogue.js.
//
// A grant-all "*" module row must expand into CONCRETE modules/tabs, otherwise a "*"-only
// role has no Administration menu and break-glass could never reach Role Management. The
// catalogue used for that expansion is DERIVED here from the single source of truth
// (ALL_UTILS in roleCatalogue.js) instead of being a hand-maintained copy (it had drifted
// silently before 2026-09-07).
//
// TRAP - do not "clean up" the Solution Management row: besides its three page tabs it
// deliberately carries the seven legacy field parts (SETTINGS_PARTS). For a role that holds
// a "*" MODULE row, those seven names are what land in metaData.partPermissions, the map
// canEditPartFor's legacy fallback reads (a "*" role normally reaches the ceiling branch
// through the expanded "Solution Edit" row; keep both rows complete).
//
// SHAPE - ALL_UTILS tabs are { name, permission } objects; this catalogue (and the consumer
// loop below) works on plain tab NAME strings. Map explicitly; never spread the objects in.
import { ALL_UTILS } from "./roleCatalogue";
import { SETTINGS_PARTS, rank } from "./permissionDependencies";

export const LEGACY_SOLUTION_MANAGEMENT_PART_TABS = SETTINGS_PARTS;

export const WILDCARD_BASE_CATALOG = ALL_UTILS.map((m) => {
  const tabs = (m.tabs || []).map((t) => t.name);
  if (m.name === "Solution Management") {
    return { name: m.name, tabs: [...new Set([...tabs, ...LEGACY_SOLUTION_MANAGEMENT_PART_TABS])] };
  }
  return { name: m.name, tabs };
});

const permOfRank = (r) => (r >= 2 ? "ALL" : r >= 1 ? "READ" : "disable");

// Merge the real (non-"*") modules with the "*"-expanded catalogue (at the "*" rank), taking the
// MAX rank per tab. Returns a "*"-free module list ready for the nav/permission derivation.
export function expandWildcardModules(roleModules) {
  const real = (roleModules || []).filter((m) => m && m.name !== "*");
  const wildRow = (roleModules || []).find((m) => m && m.name === "*");
  if (!wildRow) return real;
  const wildRank = (wildRow.tabs || []).reduce(
    (mx, t) => Math.max(mx, t && t.name === "*" ? rank(t.permission) : 0),
    0
  );
  if (wildRank < 1) return real; // a gutted "*" row confers nothing
  const wildPerm = permOfRank(wildRank);
  const byName = {};
  real.forEach((m) => {
    byName[m.name] = { name: m.name, tabs: (m.tabs || []).map((t) => ({ ...t })) };
  });
  WILDCARD_BASE_CATALOG.forEach((cat) => {
    if (!byName[cat.name]) byName[cat.name] = { name: cat.name, tabs: [] };
    const bucket = byName[cat.name];
    cat.tabs.forEach((tabName) => {
      const existing = bucket.tabs.find((t) => t && t.name === tabName);
      if (existing) {
        if (rank(existing.permission) < wildRank) existing.permission = wildPerm;
      } else {
        bucket.tabs.push({ name: tabName, permission: wildPerm });
      }
    });
  });
  return Object.values(byName);
}
